/**
 * Conversations the reader has explicitly marked unread, so nothing marks them read again.
 *
 * <p><b>Module-level, not component state, and that is the whole point.</b> Mark-read-on-open is a
 * panel effect guarded by a ref, which resets on every mount. So marking a thread unread, closing the
 * panel and having anything re-render the trigger — a summary refresh, a badge tick, a re-paged grid —
 * remounted the panel and marked it read again. From the outside that looked like the unread quietly
 * undoing itself a minute later.</p>
 *
 * <p>Daniil's rule, and it is a rule rather than a heuristic: <b>once a message has been marked unread,
 * this session sends no further read requests for that conversation.</b> Nothing clears an entry except
 * the reader explicitly marking the thread read again.</p>
 *
 * <p>Scope is the browser session by design. Persisting it would mean a thread stayed permanently
 * exempt from mark-read-on-open across reloads, which is a different and much stickier promise than the
 * one being made here.</p>
 */
const explicitlyUnread = new Set<string>()

const keyFor = (entityTypeId: string, entityId: string): string => `${entityTypeId}:${entityId}`

/** Called when the reader marks a message unread. Blocks mark-read-on-open for that conversation. */
export function suppressMarkRead(entityTypeId: string, entityId: string): void {
  explicitlyUnread.add(keyFor(entityTypeId, entityId))
}

/** Called when the reader marks read deliberately — the one action that lifts the block. */
export function allowMarkRead(entityTypeId: string, entityId: string): void {
  explicitlyUnread.delete(keyFor(entityTypeId, entityId))
}

export function isMarkReadSuppressed(entityTypeId: string, entityId: string): boolean {
  return explicitlyUnread.has(keyFor(entityTypeId, entityId))
}

/** Same check for callers that already hold a `${type}:${id}` key. */
export function isMarkReadSuppressedKey(key: string): boolean {
  return explicitlyUnread.has(key)
}
